import { useFocusEffect } from '@react-navigation/native';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { useGetProgramsQuery, useGetTagQuery } from 'services/cms';
import theme from 'theme';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';
import { capitalize } from 'utils/string';

import FocusView from 'components/FocusView';
import Section from 'components/Section';
import IsLoading from 'components/isLoading';

const CategoryScreen: React.FC<RootStackScreenProps<'Category'>> = ({ navigation, route }) => {
  const analytics = useAnalytics();
  const { data: tag, isLoading: isLoadingTag, error } = useGetTagQuery(route.params.slug);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any[]>([]);

  const { data: _data, isLoading: isLoadingPrograms } = useGetProgramsQuery(
    { [`${tag?.type}._id`]: tag?._id, page },
    { skip: !tag }
  );

  const items: CmsCard[] =
    data?.map((item) => ({
      link: `/${item.url}`,
      title: item.title,
      image: item.imageVertical,
      height: item.imageVertical.height,
      type: item.type,
    })) || [];

  useEffect(() => {
    setData((old) => [...old, ...(_data?.items || [])]);
  }, [_data]);

  useEffect(() => {
    if (tag) navigation.setOptions({ title: tag.name });
  }, [navigation, tag]);

  useFocusEffect(
    useCallback(() => {
      if (tag) {
        analytics.triggerCategoryScreenPageViewEvent({
          categoryId: tag?.id!,
          categoryName: tag?.name!,
        });
      }
    }, [tag])
  );

  if (isLoadingPrograms || isLoadingTag) {
    return (
      <View style={styles.loadingContainer}>
        <IsLoading />
      </View>
    );
  }

  if (!tag) {
    return (
      <View style={styles.container}>
        <Text style={styles.notFoundText}>Nenhum item encontrado</Text>
      </View>
    );
  }

  return (
    <FocusView trapFocusUp trapFocusRight style={{ flex: 1 }}>
      <View style={styles.contentContainer}>
        {(Platform.OS === 'web' || Platform.isTV) && (
          <Text style={styles.titleText}>{capitalize(tag?.name || '')}</Text>
        )}
        {!!items?.length && (
          <Section
            requestTVFocus
            onEndReached={() => setPage(page + 1)}
            vertical
            items={items}
            _name="section"
            showTitle={false}
            title={tag?.name || ''}
            variant="section.medium"
            {...(Platform.isTV && { topContainerHeight: calcSizeForTv(240) })}
          />
        )}
      </View>
    </FocusView>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    flex: 1,
  },
  notFoundText: {
    color: '#fff',
    marginLeft: calcSizeForDevice(32),
    fontSize: calcSizeForDevice(24),
    fontWeight: '600',
  },
  contentContainer: {
    paddingHorizontal: calcSizeForDevice(32),
    paddingBottom: calcSizeForDevice(32, 0),
    paddingTop: calcSizeForDevice(20, 120),
  },
  titleText: {
    fontSize: calcSizeForDevice(30, 48),
    color: '#fff',
    lineHeight: calcSizeForDevice(30, 60),
    fontFamily: theme.fonts.inter.bold,
    marginLeft: calcSizeForDevice(0, 15),
    marginBottom: calcSizeForDevice(60),
  },
});

export default CategoryScreen;
